import React, { useState, useRef, useEffect } from "react";
import scrollPageToTop from "../../utils/scrollPageToTop";
import usePublishModalTop from "../../utils/usePublishModalTop";



/* Optional per-tab `placeholder`: a component rendered inside the tab button after the
   label — a count, a bell, anything small. It renders inside whatever provider wraps the
   tabs, so it can read context (e.g. useSuper()). Tabs without one are unchanged. */
const InnerTabsd = ({ tabs, defaultId }) => {
  const [activeId, setActiveId] = useState(tabs[0]?.id);
  const pillRef = useRef(null);
  const btnRefs = useRef({});
  const contentRef = useRef(null);
  const areaRef = useRef(null);

  const active = tabs.find((t) => t.id === activeId) || tabs[0];
  const ActiveComponent = active?.component || null;

  /* ── Reset active tab if tabs prop changes ── */
  useEffect(() => {
    if (!tabs.find((t) => t.id === activeId)) {
      setActiveId(tabs[0]?.id);
    }
  }, [tabs, activeId]);

  /* ── Sliding pill ── */
  useEffect(() => {
  const timer = setTimeout(() => {
    const btn = btnRefs.current[activeId];
    const pill = pillRef.current;
    if (btn && pill) {
      const parent = btn.parentElement.getBoundingClientRect();
      const rect = btn.getBoundingClientRect();
      pill.style.width = `${rect.width}px`;
      pill.style.transform = `translateX(${rect.left - parent.left}px)`;
    }
  }, 50); // small delay lets the DOM finish painting

  return () => clearTimeout(timer);
}, [activeId]);

  /* ── New inner tab (or first mount): content panel and page back to the top ──
     The scroll div persists across tab changes, so a deep scroll in one tab used to carry
     over to the next; and TabBar must stay the first thing on screen. */
  useEffect(() => {
    if (contentRef.current) contentRef.current.scrollTop = 0;
    scrollPageToTop(contentRef.current);
  }, [activeId]);

  /* Modals sit UNDER the info bar on desktop too: publish where the content panel starts
     (utils/usePublishModalTop) — every fixed backdrop reads --fr-modal-top as its top. */
  usePublishModalTop(areaRef, [activeId]);

  const handleTabChange = (id) => {
    if (id === activeId) return;
    setActiveId(id);
  };

  /* ── Inline styles ── */
  const styles = {
    wrapper: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      width: "100%",
      flex: 1,          // fill .fr-tab-page > .fr-tab-content (a bounded flex column, filterTemplate.css)
      minHeight: 0,     // …so contentScroll below is the scroller and the tab row + header stay put
      boxSizing: "border-box",
    },

    /* Tab bar — 70vw centered, grid for even distribution */
    tabBarOuter: {
      width: "70vw",
      minWidth: 500,
      maxWidth: 1200,
      flexShrink: 0,
    },
    tabBar: {
      position: "relative",
      display: "grid",
      gridTemplateColumns: `repeat(${tabs.length}, 1fr)`,
      background: "rgba(255,255,255,0.04)",
      border: "1px solid rgba(255,255,255,0.1)",
      borderRadius: 14,
      padding: 4,
      marginBottom: 12,
      gap: 4,
    },
    pill: {
      position: "absolute",
      top: 4,
      left: 0,
      height: "calc(100% - 8px)",
      borderRadius: 11,
      background:
        "linear-gradient(135deg, rgba(34,211,238,0.18) 0%, rgba(59,130,246,0.18) 100%)",
      border: "1px solid rgba(34,211,238,0.3)",
      transition:
        "transform 0.35s cubic-bezier(.4,0,.2,1), width 0.35s cubic-bezier(.4,0,.2,1)",
      pointerEvents: "none",
      zIndex: 0,
    },
    tabBtn: (isActive) => ({
      position: "relative",
      zIndex: 1,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 8,
      padding: "10px 12px",
      background: "none",
      border: "none",
      borderRadius: 11,
      cursor: "pointer",
      fontSize: "0.85rem",
      fontWeight: isActive ? 600 : 500,
      color: isActive ? "#22d3ee" : "rgba(255,255,255,0.45)",
      transition: "color 0.25s ease",
      WebkitTapHighlightColor: "transparent",
      whiteSpace: "nowrap",
    }),

    ph: { display: "inline-flex", alignItems: "center" },   // wraps a tab's placeholder

    /* Header bar — same 70vw */
    headerBarOuter: {
      width: "70vw",
      minWidth: 500,
      maxWidth: 1200,
      flexShrink: 0,
    },
    headerBar: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "10px 16px",
      marginBottom: 12,
      background:
        "linear-gradient(135deg, rgba(34,211,238,0.08) 0%, rgba(59,130,246,0.08) 100%)",
      border: "1px solid rgba(34,211,238,0.2)",
      borderRadius: 12,
    },
    headerTitle: {
      fontSize: "0.95rem",
      fontWeight: 600,
      color: "#e2e8f0",
      margin: 0,
    },

    /* Content area — same 70vw */
    contentArea: {
      flex: 1,
      minHeight: 0,
      width: "70vw",
      minWidth: 500,
      maxWidth: 1200,
      boxSizing: "border-box",
      paddingBottom: 24,
      display: "flex",
      flexDirection: "column",
    },
    contentInner: {
      width: "100%",
      flex: 1,
      minHeight: 0,
      borderRadius: 16,
      border: "1px solid rgba(34,211,238,0.15)",
      background: "linear-gradient(135deg, #1e293b 0%, #0f172a 100%)",
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
    },
    contentScroll: {
      width: "100%",
      flex: 1,
      minHeight: 0,
      overflowY: "auto",
    },
  };

  return (
    <div style={styles.wrapper}>
      {/* ── Tab bar — 70vw, grid evenly spaced ── */}
      <div style={styles.tabBarOuter}>
        <div style={styles.tabBar}>
          <div ref={pillRef} style={styles.pill} />
          {tabs.map((tab) => (
            <button
              key={tab.id}
              ref={(el) => (btnRefs.current[tab.id] = el)}
              style={styles.tabBtn(tab.id === activeId)}
              onClick={() => handleTabChange(tab.id)}
              aria-selected={tab.id === activeId}
              role="tab"
            >
              {tab.icon && (
                <span style={{ display: "flex", alignItems: "center" }}>
                  {tab.icon}
                </span>
              )}
              {tab.label}
              {tab.placeholder && (
                <span data-ph="" style={styles.ph}><tab.placeholder /></span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* ── Header bar ── */}
      <div style={styles.headerBarOuter}>
        <div style={styles.headerBar}>
          <p style={styles.headerTitle}>
            {active.icon && (
              <span
                style={{
                  marginRight: 6,
                  verticalAlign: "middle",
                  display: "inline-flex",
                }}
              >
                {active.icon}
              </span>
            )}
            {active.label}
          </p>
          
        </div>
      </div>

      {/* ── Content area — 70vw ── */}
      <div ref={areaRef} style={styles.contentArea}>
        <div style={styles.contentInner}>
          <div ref={contentRef} style={styles.contentScroll}>
            
              {ActiveComponent && <ActiveComponent />}
            
          </div>
        </div>
      </div>
    </div>
  );
};

export default InnerTabsd;